/**
 * SpinEffect — wraps a child in a continuously-rotating Reanimated view.
 *
 * Used for "syncing" / "applying changes" indicators where the rotation
 * itself communicates that a background process is in flight. Direction
 * is configurable: clockwise mirrors the conventional "refresh" gesture;
 * counter-clockwise mirrors the `🔄` codepoint used in product copy.
 *
 * The wrapper inherits its size from the child — give the child explicit
 * `width` / `height` so the rotation appears to spin in place.
 *
 * @example
 * ```tsx
 * <SpinEffect>
 *   <Icon name="cycle" size={14} color={theme.colors.status.success} />
 * </SpinEffect>
 * ```
 */
import { memo, useEffect, type FC, type ReactNode } from "react";
import Animated, {
  Easing,
  cancelAnimation,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from "react-native-reanimated";

export type SpinDirection = "clockwise" | "counter-clockwise";

export interface SpinEffectProps {
  readonly children: ReactNode;
  /** Time for one full rotation, in ms. Default 1500. */
  readonly durationMs?: number;
  /** Rotation direction. Default "counter-clockwise" to match the `🔄` glyph. */
  readonly direction?: SpinDirection;
  /** Pause the rotation (still mounted, just held at 0deg). */
  readonly paused?: boolean;
}

export const SpinEffect: FC<SpinEffectProps> = memo(
  ({ children, durationMs = 1500, direction = "counter-clockwise", paused = false }) => {
    const progress = useSharedValue(0);

    useEffect(() => {
      if (paused) {
        cancelAnimation(progress);
        progress.value = 0;
        return;
      }
      progress.value = 0;
      progress.value = withRepeat(
        withTiming(1, { duration: durationMs, easing: Easing.linear }),
        -1, // infinite
        false, // don't reverse — keep spinning the same way
      );
      return () => {
        cancelAnimation(progress);
      };
    }, [durationMs, paused, progress]);

    const animatedStyle = useAnimatedStyle(() => {
      const turns = direction === "clockwise" ? progress.value * 360 : progress.value * -360;
      return { transform: [{ rotateZ: `${String(turns)}deg` }] };
    }, [progress, direction]);

    return <Animated.View style={animatedStyle}>{children}</Animated.View>;
  },
);

SpinEffect.displayName = "SpinEffect";
